import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/axios.ts';
import type { TenantListResponse, TenantRow } from '@/shared/types/tenant.ts';
import { useDebounce } from './useDebounce.ts';

export interface TenantOption {
  id: string;
  nome: string;
  slug: string;
}

/** Server-side search — debounced, returns top 20 matches */
export function useTenantsSearch(search: string) {
  const debounced = useDebounce(search.trim(), 300);

  return useQuery({
    queryKey: ['tenants', 'search', debounced],
    queryFn: async () => {
      const { data } = await api.get<TenantListResponse>('/admin/tenants', {
        params: { page: 1, limit: 20, isActive: true, search: debounced || undefined },
      });
      return data.data.map((t): TenantOption => ({ id: t.id, nome: t.nome, slug: t.slug }));
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Resolve a single tenant by ID (for displaying selected label) */
export function useTenantById(id: string | undefined) {
  return useQuery({
    queryKey: ['tenants', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get<TenantRow>(`/admin/tenants/${id}`);
      return { id: data.id, nome: data.nome, slug: data.slug } as TenantOption;
    },
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

/** Load all active tenants — for TenantMultiSelect (multi-select use case) */
export function useActiveTenants() {
  return useQuery({
    queryKey: ['tenants', 'active'],
    queryFn: async () => {
      const { data } = await api.get<TenantListResponse>('/admin/tenants', {
        params: { page: 1, limit: 1000, isActive: true },
      });
      return data.data.map((t): TenantOption => ({ id: t.id, nome: t.nome, slug: t.slug }));
    },
    staleTime: 5 * 60_000,
  });
}
